import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { Form, InputGroup } from "react-bootstrap";
import Select from "react-select";
import RequiredLabel from "../../CustomCommons/RequiredLabel";
import { BASE_URL } from "../../Const/Url";
import { showToast } from "../../../utils/ToastHelper";
import { logout_func2 } from "../../Const/logoutCommon";
import SelectOpenWithPagginationDynamic from "../../Const/SelectOpenWithPagginationDynamic";
import Loader from "../../CustomCommons/Loader";

const EditCustomReview = () => {
  const history = useHistory();
  const { slug } = useParams(); // using "slug" as identifier (id)

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [star, setStar] = useState(5);
  const [descriptions, setDescriptions] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [selectedProduct, setSelectedProduct] = useState([]);
  const [productList, setProductList] = useState([]);
  const [productQuery, setProductQuery] = useState("");

  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userQuery, setUserQuery] = useState("");

  const [photoIds, setPhotoIds] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]); // {id, name, url}

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [, setOnlogoutHide] = useState(0);

  const uploadDocument = (file, onSuccess, docType = 0) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file);
    formData.append("doc_type", docType);

    axios
      .post(`${BASE_URL}api/v1/auth/documents/upload/`, formData)
      .then((res) => {
        if (res?.data?.status) {
          onSuccess(res?.data?.data?.id);
          showToast("success", "File uploaded successfully");
        } else {
          showToast("error", "Failed to upload file");
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.errors?.document?.[0] ||
          err?.response?.data?.message ||
          "Upload failed";
        showToast("error", message);
      });
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      uploadDocument(file, (id) => {
        setPhotoIds((prev) => [...prev, id]);
        const reader = new FileReader();
        reader.onload = () => {
          setPhotoPreviews((prev) => [
            ...prev,
            { id, name: file.name, url: reader.result },
          ]);
        };
        reader.readAsDataURL(file);
      });
    });
  };

  const removePhoto = (idToRemove) => {
    setPhotoIds((prev) => prev.filter((id) => id !== idToRemove));
    setPhotoPreviews((prev) => prev.filter((p) => p.id !== idToRemove));
  };

  const handleProductChange = (newValue) => {
    // Enforce single product selection: keep only the last selected option.
    if (!newValue || !newValue.length) {
      setSelectedProduct([]);
      return;
    }
    const lastSelected = newValue[newValue.length - 1];
    setSelectedProduct([lastSelected]);
  };

  const handleProductInput = (inputValue) => {
    setProductQuery(inputValue);
  };

  const fetchUsers = (query = "") => {
    // Website users (customers)
    const params = new URLSearchParams();
    params.append("is_customer", true);
    params.append("limit", 100);
    if (query) {
      params.append("query", query);
    }

    axios
      .get(`${BASE_URL}api/v1/users/inventory/user/?${params.toString()}`)
      .then((res) => {
        const data = res?.data?.data || [];
        const formatted = data.map((user) => {
          const fullName = `${user?.first_name || ""} ${
            user?.last_name || ""
          }`.trim();
          return {
            value: user?.id,
            label: fullName || user?.email || "Unknown user",
            first_name: user?.first_name || "",
            last_name: user?.last_name || "",
          };
        });
        setUserOptions(formatted);
      })
      .catch(() => {});
  };

  // Initial user list
  useEffect(() => {
    fetchUsers("");
  }, []);

  // Debounced user search when typing, only if length > 3
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (!userQuery) {
        fetchUsers("");
        return;
      }
      if (userQuery.length > 3) {
        fetchUsers(userQuery);
      }
    }, 400);
    return () => clearTimeout(debounce);
  }, [userQuery]);

  const fetchReviewDetails = () => {
    if (!slug) {
      showToast("error", "Missing review identifier.");
      history.push("/website-settings/custom-review-list");
      return;
    }

    setIsFetching(true);
    axios
      .get(`${BASE_URL}api/v1/promotions/admin/review/${slug}/`)
      .then((res) => {
        const data = res?.data?.data;
        if (!data) {
          showToast("error", "Failed to load review details.");
          history.push("/website-settings/custom-review-list");
          return;
        }

        setFirstName(data?.first_name || "");
        setLastName(data?.last_name || "");
        setStar(data?.star ?? 5);
        setDescriptions(data?.descriptions || "");
        setIsActive(data?.is_active ?? true);

        // Product
        if (data?.product_details?.id) {
          const productOption = {
            value: data.product_details.id,
            label: data.product_details.name || "Unknown product",
          };
          setSelectedProduct([productOption]);
          // Ensure it appears in list for the selector
          setProductList((prev) => {
            const exists = prev?.some((p) => p.value === productOption.value);
            return exists ? prev : [...(prev || []), productOption];
          });
        }

        // Reviewer
        if (data?.reviewed_by) {
          const userOption = {
            value: data.reviewed_by,
            label: data.reviewed_by_name || "Unknown user",
            first_name: data?.first_name || "",
            last_name: data?.last_name || "",
          };
          setSelectedUser(userOption);
          // Also sync the first/last name inputs (already done above)
        }

        // Photos
        if (Array.isArray(data?.photos_details)) {
          const ids = data.photos_details.map((p) => p.id);
          const previews = data.photos_details.map((p) => ({
            id: p.id,
            name: p.document || `Photo ${p.id}`,
            url: p.resized_doc_url || p.doc_url || "",
          }));
          setPhotoIds(ids);
          setPhotoPreviews(previews);
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        showToast("error", "Unable to fetch review details.");
        history.push("/website-settings/custom-review-list");
      })
      .finally(() => setIsFetching(false));
  };

  useEffect(() => {
    fetchReviewDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const validateForm = () => {
    if (!firstName.trim()) {
      showToast("error", "First name is required.");
      return false;
    }
    if (!lastName.trim()) {
      showToast("error", "Last name is required.");
      return false;
    }
    if (!descriptions.trim()) {
      showToast("error", "Description is required.");
      return false;
    }
    if (!selectedProduct?.length) {
      showToast("error", "Please select a product.");
      return false;
    }
    if (!selectedUser?.value) {
      showToast("error", "Please select a reviewer.");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      star: Number(star),
      descriptions: descriptions.trim(),
      is_active: isActive,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      product: selectedProduct?.[0]?.value || 0,
      reviewed_by: selectedUser?.value,
      photos: photoIds,
    };

    setIsSubmitting(true);
    axios
      .patch(`${BASE_URL}api/v1/promotions/admin/review/${slug}/`, payload)
      .then(() => {
        showToast("success", "Review updated successfully.");
        history.push("/website-settings/custom-review-list");
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        const message =
          err?.response?.data?.errors?.non_field_errors?.[0] ||
          err?.response?.data?.message ||
          "Failed to update review. Please try again.";
        showToast("error", message);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCancel = () => {
    history.push("/website-settings/custom-review-list");
  };

  if (isFetching) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent new-categorey-parent an">
      <div className="bg-white add-div">
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: "20px 35px", border: "0.5px solid #E0E0E0" }}
        >
          <h3 style={{ fontSize: "23px", marginTop: "6px" }}>
            Edit Custom Review
          </h3>
        </div>

        <div
          style={{
            padding: "20px 35px",
            borderBottom: "0.5px solid #E0E0E0",
            borderRight: "0.5px solid #E0E0E0",
            borderLeft: "0.5px solid #E0E0E0",
          }}
          className="row"
        >
          <div className="col-12 col-md-6">
            <RequiredLabel text="First Name" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="First Name"
                style={{ backgroundColor: "#FAFAFA" }}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Last Name" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Last Name"
                style={{ backgroundColor: "#FAFAFA" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Star Rating" />
            <Form.Select
              className="mb-3"
              value={star}
              onChange={(e) => setStar(Number(e.target.value))}
              style={{ height: "47px", backgroundColor: "#FAFAFA" }}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
            </Form.Select>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Reviewed By (User)" />
            <Select
              placeholder="Select reviewer"
              value={selectedUser}
              onChange={(value) => {
                setSelectedUser(value);
                if (value) {
                  setFirstName(value.first_name || "");
                  setLastName(value.last_name || "");
                }
              }}
              onInputChange={(value) => setUserQuery(value)}
              options={userOptions}
              isClearable
            />
          </div>

          <div className="col-12 mt-3">
            <RequiredLabel text="Product" />
            <div className="position-relative">
              <SelectOpenWithPagginationDynamic
                handleMultiInputChange={handleProductChange}
                inputProduct={handleProductInput}
                ProductsList={productList}
                defaultValue={selectedProduct}
                setProductsList={setProductList}
                inputQuery={productQuery}
                url1={`api/v1/inventory/inventory/product_list_for_offer/?is_website=true&is_admin=false&is_outlet=false&limit=20`}
                url2={`api/v1/inventory/inventory/product_list_for_offer/?query=${productQuery}&is_website=true&is_admin=false&is_outlet=false&limit=20`}
              />
            </div>
          </div>

          <div className="col-12 mt-3">
            <RequiredLabel text="Description" />
            <InputGroup className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                style={{ backgroundColor: "#FAFAFA" }}
                value={descriptions}
                onChange={(e) => setDescriptions(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 mt-3">
            <RequiredLabel text="Photos" />
            <p style={{ marginTop: "10px", color: "red" }}>
              You can upload multiple images. They will be stored as document
              IDs.
            </p>
            <div
              className="w-100 browse-main d-flex align-items-center"
              style={{
                height: "55px",
                border: "1px solid #E0E0E0",
                borderRadius: "7px",
                overflowX: "hidden",
              }}
            >
              <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                {photoPreviews?.length
                  ? `${photoPreviews.length} file(s) selected`
                  : "No File Chosen"}
              </p>
              <div className="file-up">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                />
                <p>Browse</p>
              </div>
            </div>
            {photoPreviews?.length > 0 && (
              <div className="d-flex flex-wrap mt-2 gap-2">
                {photoPreviews.map((photo) => (
                  <div key={photo.id} className="position-relative">
                    <img
                      src={photo.url}
                      alt={photo.name}
                      height={70}
                      width={70}
                      style={{ objectFit: "cover", borderRadius: "4px" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute"
                      style={{ top: "-8px", right: "-8px" }}
                      onClick={() => removePhoto(photo.id)}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="col-12 mt-4">
            <h5>Status</h5>
            <p style={{ color: "#8E8E93" }}>( Toggle review visibility )</p>
            <div
              className="d-flex justify-content-between align-items-center py-3 px-3"
              style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
            >
              <span>{isActive ? "Active" : "Inactive"}</span>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSubmit}
              className="save-btn me-2 mt-2"
              style={{ background: "#000", color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </button>
            <button
              onClick={handleCancel}
              className="save-btn mt-2"
              style={{ border: ".4px solid #E0E0E0 !important" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCustomReview;
